import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FormProvider, useForm, useFieldArray } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEdit,
  faClose,
  faPlus,
  faTrash,
  faEye,
  faHome,
  faBook,
  faList
} from "@fortawesome/free-solid-svg-icons";
import { sendError, sendSuccess } from "../helpers/Helper";
import { Model } from "../components/Model";
import { Textbox } from "../components/Textbox";
import { SelectBox } from "../components/SelectBox";
import SignatureUploader from "../components/SignatureUploader";
import { API_URL_ENDPOINT } from "../appsetting";
import { useNavigate, useParams } from "react-router-dom";
import { useApi } from "../helpers/useApi";
import { Routess } from "../Routes/routes";
import { theme } from "../constants/theme";
import OffCanvas from "../components/OffCanvas";
import AddPageImage from "../assets/img/addPage.png";
import "react-quill/dist/quill.snow.css";
import JoditEditor from "jodit-react";
import NoCover from "../assets/img/nocover.png";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import DropzoneUpload from "../components/DropzoneUpload";

export const BookDetail = () => {
  const callApi = useApi();
  const { id } = useParams();
  const navigate = useNavigate();

  const [book, setBook] = useState({});
  const [coverPreview, setCoverPreview] = useState(null);
  const [attributes, setAttributes] = useState([]);
  const [category, setCategory] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPages, setShowPages] = useState(false);
  const [imagePages, setImagePage] = useState([]);
  const [textPages, setTextPage] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [showAddImageModel, setShowImageModel] = useState(false);
  const [showAddTextModel, setShowTextModel] = useState(false);
  const [editorValue, setEditorValue] = useState("");
  const [activeTab, setActiveTab] = useState("editor");
  const [isEditingPage, setIsEditingPage] = useState(false);
  const [editingPageId, setEditingPageId] = useState(null);
  const [editPDf, setShowEditPDF] = useState(false);

  // 📘 Main Book Edit Form
  const bookForm = useForm({
    defaultValues: {
      id: "0",
      title: "",
      description: "",
      bookAttributes: [],
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
    watch,
  } = bookForm;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "bookAttributes",
  });

  useEffect(() => {
    if (book?.contentType === "ImagePages") {
      const current = imagePages.find((p) => p.pageNumber === currentPage);
      if (!current || !current.imageUrl) {
        getImagePage(currentPage);
      }
    }
    if (book?.contentType === "TextPages") {
      const current = textPages.find((p) => p.pageNumber === currentPage);
      if (!current || !current.imageUrl) {
        getTextPage(currentPage);
      }
    }
  }, [currentPage]);

  // 🎹 Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showPages) return;

      const pages = imagePages?.length > 0 ? imagePages : textPages;
      if (!pages || pages.length === 0) return;

      if (e.key === "ArrowLeft") {
        const sorted = [...pages].sort((a, b) => a.pageNumber - b.pageNumber);
        const idx = sorted.findIndex((p) => p.pageNumber === currentPage);
        if (idx > 0) setCurrentPage(sorted[idx - 1].pageNumber);
      } else if (e.key === "ArrowRight") {
        const sorted = [...pages].sort((a, b) => a.pageNumber - b.pageNumber);
        const idx = sorted.findIndex((p) => p.pageNumber === currentPage);
        if (idx !== -1 && idx < sorted.length - 1) setCurrentPage(sorted[idx + 1].pageNumber);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showPages, currentPage, imagePages, textPages]);

  // --- API Calls ---
  const fetchBook = useCallback(async () => {
    try {
      setLoading(true);
      const response = await callApi(`${API_URL_ENDPOINT.Books}/${id}`);
      if (!response.status || !response.data) {
        navigate(Routess.Books.path);
        return;
      }
      setBook(response.data);
      setImagePage(response.data?.imagePages);
      setTextPage(response.data?.textPages);
      setIsPublished(response.data.isPublished ?? false);
    } catch (err) {
      sendError("Unable to load book data");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAttributes = useCallback(async () => {
    try {
      const response = await callApi(`${API_URL_ENDPOINT.Books}/attributes`);
      if (response.status) setAttributes(response.data ?? []);
    } catch {
      sendError("Failed to load attributes");
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await callApi(`${API_URL_ENDPOINT.Books}/category?pageSize:1000000`);
      if (response.status) setCategory(response.data?.items ?? []);
    } catch {
      sendError("Failed to load attributes");
    }
  }, []);

  const updateBook = async (formValues) => {
    try {
      const payload = {
        id,
        title: formValues.title,
        categoryID: formValues?.category ?? null,
        coverImage: coverPreview?.fileKey ?? "",
        description: formValues.description,
        attributes: formValues.bookAttributes.map((a) => ({
          attributeID: parseInt(a.attributeId, 10),
          attributeValue: a.attributeValue,
        })),
      };

      const response = await callApi(API_URL_ENDPOINT.Books, {
        method: "POST",
        data: payload,
      });

      if (response.status) {
        sendSuccess(response.message);
        setCoverPreview(null);
        setShowEditModal(false);
        fetchBook();
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  const saveTextPage = async (data) => {
    try {
      if (editorValue == "" || editorValue == null) return;
      const payload = {
        bookID: id,
        pageContent: data.pageContent,
      };

      const response = await callApi(API_URL_ENDPOINT.Books + "/page-text", {
        method: "POST",
        data: payload,
      });

      if (response.status) {
        sendSuccess(response.message);
        setShowTextModel(false);
        fetchBook();
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  const getImagePage = async (pageNumber) => {
    try {
      if (pageNumber === 0) return;
      const exists = imagePages?.find((p) => p.pageNumber === pageNumber);
      if (exists?.imageUrl) return;
      setPageLoading(true);

      const response = await callApi(
        `${API_URL_ENDPOINT.Books}/page-image/${exists?.id ?? pageNumber}`,
        { method: "GET" }
      );

      if (response.status && response.data) {
        setImagePage((prev) =>
          prev.map((p) =>
            p.pageNumber === pageNumber ? { ...p, ...response.data } : p
          )
        );
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message || "Failed to fetch image page");
    } finally {
      setPageLoading(false);
    }
  };

  const getTextPage = async (pageNumber) => {
    try {
      if (pageNumber === 0) return;
      const exists = textPages?.find((p) => p.pageNumber === pageNumber);
      if (exists?.content) return;
      setPageLoading(true);

      const response = await callApi(
        `${API_URL_ENDPOINT.Books}/page-text/${exists?.id ?? pageNumber}`,
        { method: "GET" }
      );

      if (response.status && response.data) {
        setTextPage((prev) =>
          prev.map((p) =>
            p.pageNumber === pageNumber ? { ...p, ...response.data } : p
          )
        );
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message || "Failed to fetch image page");
    } finally {
      setPageLoading(false);
    }
  };

  const saveCoverToDB = async (data) => setCoverPreview(data);

  const togglePublish = async (status) => {
    try {
      setIsPublished(status);
      const response = await callApi(
        `${API_URL_ENDPOINT.Books}/publish/${id}?status=${status}`,
        { method: "GET" }
      );
      if (response.status) fetchBook();
      else throw new Error(response.message);
    } catch (ex) {
      sendError(ex.message);
    }
  };

  useEffect(() => {
    fetchAttributes();
    fetchCategories();
    fetchBook();
  }, []);

  useEffect(() => {
    if (book?.id) {
      reset({
        ...book,
        category: book.categoryID || book.categoryId, // Initialize category field for SelectBox
      });
    }
  }, [book]);

  const viewBook = async () => {
    if (book.contentType === "PDF") {
      const pdfUrl = encodeURIComponent(book?.pdfFile?.fileUrl);
      const viewerUrl = `https://docs.google.com/gview?url=${pdfUrl}&embedded=true`;
      window.open(viewerUrl, "_blank", "noopener,noreferrer");
    } else {
      setShowPages(true);
      setCurrentPage(1);
    }
  };

  // Helper variables for pagination
  const allPages = imagePages?.length > 0 ? imagePages : textPages;
  const maxPage = allPages && allPages.length > 0 ? Math.max(...allPages.map(p => p.pageNumber)) : 0;

  const prevPage = () => {
    if (!allPages) return;
    const sorted = [...allPages].sort((a, b) => a.pageNumber - b.pageNumber);
    const idx = sorted.findIndex(p => p.pageNumber === currentPage);
    if (idx > 0) setCurrentPage(sorted[idx - 1].pageNumber);
  };

  const nextPage = () => {
    if (!allPages) return;
    const sorted = [...allPages].sort((a, b) => a.pageNumber - b.pageNumber);
    const idx = sorted.findIndex(p => p.pageNumber === currentPage);
    if (idx !== -1 && idx < sorted.length - 1) setCurrentPage(sorted[idx + 1].pageNumber);
  };

  const AddPage = async () => {
    setIsEditingPage(false);
    setEditingPageId(null);
    setEditorValue("");
    setCoverPreview(null);
    if (book.contentType === "ImagePages") {
      setShowImageModel(true);
      setCoverPreview({ documentURL: AddPageImage });
    }
    if (book.contentType === "TextPages") {
      setShowPages(false);
      setTimeout(() => setShowTextModel(true), 350);
    }
  };

  const handleEditPage = () => {
    const imgPage = imagePages?.find((p) => p.pageNumber === currentPage);
    const txtPage = textPages?.find((p) => p.pageNumber === currentPage);

    setIsEditingPage(true);

    if (imgPage) {
      setEditingPageId(imgPage.id);
      setCoverPreview({ documentURL: imgPage.imageUrl, fileKey: null });
      setShowImageModel(true);
    } else if (txtPage) {
      setEditingPageId(txtPage.id);
      setEditorValue(txtPage.content);
      setShowPages(false);
      setTimeout(() => setShowTextModel(true), 350);
    } else {
      sendError("Page data not found.");
    }
  };

  const saveImageAddPage = async () => {
    try {
      if (!isEditingPage && coverPreview?.fileKey == null) return;
      if (isEditingPage && coverPreview?.documentURL == null && coverPreview?.fileKey == null) return;

      const payload = {
        bookID: id,
        ...(isEditingPage && { id: editingPageId }),
        images: coverPreview?.fileKey ? [coverPreview.fileKey] : [],
      };

      const method = isEditingPage ? "PUT" : "POST";
      const response = await callApi(API_URL_ENDPOINT.Books + "/page-image", {
        method: method,
        data: payload,
      });

      if (response.status) {
        sendSuccess(response.message);
        setCoverPreview(null);
        setShowImageModel(false);
        fetchBook();
        if (isEditingPage) setShowPages(true);
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  const saveTextAddPage = async () => {
    try {
      if (editorValue == "" || editorValue == null) return;
      const payload = {
        "bookID": id,
        ...(isEditingPage && { id: editingPageId }),
        "pageTitle": null,
        "content": editorValue
      };

      const method = isEditingPage ? "POST" : "POST";
      const response = await callApi(API_URL_ENDPOINT.Books + "/page-text", {
        method: method,
        data: payload,
      });

      if (response.status) {
        sendSuccess(response.message);
        setEditorValue("");
        setShowTextModel(false);
        fetchBook();
        setShowPages(true);
      } else {
        throw new Error(response.message);
      }
    } catch (ex) {
      sendError(ex.message);
    }
  };

  const editorConfig = useMemo(() => ({
    height: 400,
    toolbarAdaptive: false,
    placeholder: 'Start writing your page content here...',
    showCharsCounter: false,
    showWordsCounter: false,
    showXPathInStatusbar: false,
    statusbar: false,
    askBeforePasteFromWord: false,
    askBeforePasteHTML: false,
    controls: {
      font: {
        list: {
          'Poppins,sans-serif': 'Poppins',
          'Tiro Devanagari Hindi, serif': 'Tiro Devanagari Hindi',
          'Noto Sans Devanagari, sans-serif': 'Noto Sans Devanagari',
          'Arial,Helvetica,sans-serif': 'Arial',
          'Georgia,serif': 'Georgia',
          'Impact,Charcoal,sans-serif': 'Impact',
          'Tahoma,Geneva,sans-serif': 'Tahoma',
          'Times New Roman,Times,serif': 'Times New Roman',
          'Verdana,Geneva,sans-serif': 'Verdana',
        }
      }
    },
    buttons: "bold,italic,underline,strikethrough,|,font,fontsize,brush,paragraph,|,ul,ol,|,align,|,link,image,|,undo,redo,hr",
  }), []);

  // --- Render ---
  return (
    <div className="w-full">
      {/* 🔹 Gradient Breadcrumb Header */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4 bg-white p-4 rounded-xl shadow-sm border border-lightGray mt-4">
        <div>
          <div className="flex items-center mt-0">
            <button
              className="w-10 h-10 rounded-full border border-lightGray flex items-center justify-center mr-3 hover:bg-light hover:text-primary transition-colors text-gray"
              onClick={() => navigate(-1)}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <div>
              <h4 className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-br from-primary to-accent mb-0">
                Book Details
              </h4>
              <small className="text-gray text-xs block">Manage and view book information</small>
            </div>
          </div>
        </div>
      </div>

      {/* 🔹 Main Hero Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-lightGray overflow-hidden mb-6">
        {loading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row">
            {/* 🖼️ Left Column: Cover Image */}
            <div className="md:w-1/4 bg-light p-6 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-lightGray">
              <div className="relative shadow-lg rounded-xl overflow-hidden w-full max-w-[200px] mb-4">
                <img
                  src={book?.coverImage || NoCover}
                  alt={book.title}
                  className="w-full h-auto object-cover aspect-[2/3]"
                />
                {/* Status Overlay */}
                <div className={`absolute top-0 left-0 w-full p-2 text-center text-white font-bold text-xs uppercase tracking-wide opacity-95 ${isPublished ? 'bg-success' : 'bg-secondary'}`}>
                  {isPublished ? 'PUBLISHED' : 'DRAFT'}
                </div>
              </div>

              <div className="flex items-center gap-2 mt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isPublished}
                    onChange={(e) => togglePublish(e.target.checked)}
                  />
                  <div className="relative w-11 h-6 bg-gray-100 border border-gray-300 rounded-full peer peer-focus:ring-2 peer-focus:ring-orange-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[1px] after:left-[1px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary peer-checked:border-primary"></div>
                  <span className="ml-3 text-sm font-medium text-dark">{isPublished ? "Published" : "Draft Mode"}</span>
                </label>
              </div>
            </div>

            {/* 📝 Right Column: Details */}
            <div className="md:w-3/4 p-6 md:p-8 flex flex-col">
              <div className="flex flex-col md:flex-row justify-between items-start mb-6 gap-4">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-100 mb-2">
                    {book?.categoryTitle || 'Uncategorized'}
                  </span>
                  <h2 className="text-3xl font-bold text-dark mb-1 font-poppins">{book?.title}</h2>
                  <div className="flex items-center gap-2 text-gray text-sm">
                    <FontAwesomeIcon icon={book.contentType === 'PDF' ? faFilePdf : faEye} className="text-primary" />
                    <span>{book?.contentType} Format</span>
                    <span>•</span>
                    <span>Added on {new Date().toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="flex items-center gap-2 px-4 py-2 border border-lightGray rounded-xl text-dark hover:bg-light transition-colors font-medium text-sm"
                  >
                    <FontAwesomeIcon icon={faEdit} className="text-warning" />
                    <span className="hidden md:inline">Edit</span>
                  </button>
                  <button
                    onClick={viewBook}
                    className="flex items-center gap-2 px-5 py-2 bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-xl shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all font-medium text-sm"
                  >
                    <FontAwesomeIcon icon={faEye} />
                    <span className="hidden md:inline">Read Book</span>
                  </button>
                </div>
              </div>

              <div className="mb-8">
                <h6 className="text-xs font-bold text-gray uppercase tracking-wider mb-2">Description</h6>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {book?.description || "No description available for this book."}
                </p>
              </div>

              <div className="mt-auto">
                <h6 className="text-xs font-bold text-gray uppercase tracking-wider mb-4 border-b border-lightGray pb-2">Attributes & Metadata</h6>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {book?.bookAttributes?.length > 0 ? (
                    book.bookAttributes.map((attr, idx) => (
                      <div key={idx}>
                        <div className="text-[10px] text-gray uppercase tracking-wider font-bold">{attr.attributeTitle}</div>
                        <div className="text-dark font-semibold text-sm">{attr.attributeValue}</div>
                      </div>
                    ))
                  ) : (
                    <div className="col-span-full text-gray text-sm italic">No additional attributes set.</div>
                  )}
                </div>

                {book?.contentType === "PDF" && (
                  <div className="mt-6 pt-4 border-t border-lightGray">
                    <button
                      onClick={() => setShowEditPDF(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 rounded-lg text-sm font-semibold transition-colors"
                    >
                      <FontAwesomeIcon icon={faFilePdf} />
                      Manage PDF File
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 🔹 Edit Book Modal */}
      <Model show={showEditModal} size="xl">
        <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-accent/20 text-white rounded-t-2xl">
          <div className="flex items-center">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center mr-3 shadow-lg shadow-primary/30">
              <FontAwesomeIcon icon={faEdit} className="text-white text-sm" />
            </div>
            <h5 className="font-bold text-base m-0">Edit Book Details</h5>
          </div>
          <button
            onClick={() => setShowEditModal(false)}
            className="w-8 h-8 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all text-white"
          >
            <FontAwesomeIcon icon={faClose} />
          </button>
        </div>

        <div className="bg-light">
          <FormProvider {...bookForm}>
            <form onSubmit={handleSubmit(updateBook)}>
              <div className="flex flex-col lg:flex-row">
                {/* Left Column */}
                <div className="lg:w-1/4 p-6 bg-white border-r border-lightGray text-center">
                  <h6 className="text-sm font-bold text-dark mb-4 text-left">📖 Book Cover</h6>
                  <div className="mx-auto mb-4 w-[180px] h-[270px] rounded-xl overflow-hidden shadow-md border border-lightGray relative">
                    <SignatureUploader
                      imageURL={coverPreview?.documentURL || book?.coverImage || NoCover}
                      onUploaded={saveCoverToDB}
                      height={270}
                      width={180}
                      showEdit
                    />
                  </div>
                  <p className="text-xs text-gray mb-4">Click to update cover.</p>
                  <div className="flex flex-col gap-2">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-2 rounded-lg bg-gradient-to-br from-primary to-orange-600 text-white font-semibold text-sm shadow-md hover:shadow-lg disabled:opacity-70"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditModal(false)}
                      className="w-full py-2 rounded-lg bg-white border border-lightGray text-gray font-semibold text-sm hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
                {/* Right Column */}
                <div className="lg:w-3/4 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <Textbox
                      Label="Book Title"
                      name="title"
                      register={register}
                      validationRules={{ required: "Title is required" }}
                      errors={errors}
                      watch={watch}
                    />
                    <SelectBox
                      Label="Category"
                      name="category"
                      register={register}
                      errors={errors}
                      watch={watch}
                    >
                      <option value="">Select Category</option>
                      {category.map(c => (
                        <option key={c.id} value={c.id}>{c.title}</option>
                      ))}
                    </SelectBox>
                  </div>

                  <div className="mb-4">
                    <Textbox
                      Label="Description"
                      name="description"
                      register={register}
                      errors={errors}
                      watch={watch}
                      style={{ height: 'auto' }}
                      as="textarea"
                      rows={4}
                    />
                  </div>

                  <div className="mt-6 pt-4 border-t border-lightGray">
                    <div className="flex justify-between items-center mb-4">
                      <h6 className="text-sm font-bold text-dark m-0">Dynamic Attributes</h6>
                      <button
                        type="button"
                        onClick={() => append({ attributeId: "", attributeValue: "" })}
                        className="px-3 py-1.5 rounded-lg bg-light text-primary border border-primary/20 text-xs font-bold hover:bg-primary hover:text-white transition-colors flex items-center gap-1"
                      >
                        <FontAwesomeIcon icon={faPlus} /> Add Attribute
                      </button>
                    </div>

                    <div className="space-y-3">
                      {fields.map((field, index) => (
                        <div key={field.id} className="flex gap-3 items-start p-3 bg-white border border-lightGray rounded-xl shadow-sm">
                          <div className="flex-1">
                            <SelectBox
                              name={`bookAttributes.${index}.attributeId`}
                              register={register}
                              errors={errors}
                              watch={watch}
                              wrapperClassName="mb-0"
                              validationRules={{ required: true }}
                            >
                              <option value="">Select Attribute</option>
                              {attributes.map(attr => (
                                <option key={attr.id} value={attr.id}>{attr.name}</option>
                              ))}
                            </SelectBox>
                          </div>
                          <div className="flex-1">
                            <Textbox
                              name={`bookAttributes.${index}.attributeValue`}
                              register={register}
                              errors={errors}
                              watch={watch}
                              Placeholder="Value"
                              validationRules={{ required: true }}
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="mt-1 p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      ))}
                      {fields.length === 0 && (
                        <p className="text-center text-gray text-xs py-4 border border-dashed border-gray-300 rounded-xl">
                          No attributes added yet. Click "Add Attribute" to start.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      </Model>

      {/* 🔹 PDF Upload Modal */}
      {book?.contentType === "PDF" && (
        <Model show={editPDf} size="lg">
          <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-2xl">
            <h5 className="font-bold text-base m-0 px-2 flex items-center gap-2">
              <FontAwesomeIcon icon={faFilePdf} /> Manage PDF
            </h5>
            <button onClick={() => setShowEditPDF(false)} className="text-white hover:text-white/80"><FontAwesomeIcon icon={faClose} /></button>
          </div>
          <div className="p-6">
            <div className="p-8 border-2 border-dashed border-gray-300 rounded-xl bg-light text-center">
              <DropzoneUpload
                fileType="pdf"
                onUploaded={(data) => {
                  // Handle PDF upload success
                  // Ideally should update book with new PDF key
                  setShowEditPDF(false);
                  fetchBook();
                }}
                uploadUrl={`${API_URL_ENDPOINT.Books}/page-pdf`}
                additionalData={{ bookID: id }}
              />
            </div>
          </div>
        </Model>
      )}

      {/* OffCanvas for Pages */}
      <OffCanvas
        show={showPages}
        handleClose={() => setShowPages(false)}
        title="Book Pages"
        placement="end"
        width="100%"
      >
        <div className="flex h-full bg-light">
          {/* Left Panel: Content */}
          <div className="flex-1 overflow-hidden relative flex flex-col items-center justify-center p-6 bg-slate-100">
            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <button onClick={prevPage} className="p-3 bg-white rounded-full shadow-lg hover:bg-light transition-colors text-dark" disabled={currentPage <= 1}>
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            </div>
            <div className="absolute top-4 right-4 z-10 flex gap-2">
              <button onClick={nextPage} className="p-3 bg-white rounded-full shadow-lg hover:bg-light transition-colors text-dark" disabled={currentPage >= maxPage}>
                <FontAwesomeIcon icon={faArrowLeft} style={{ transform: 'rotate(180deg)' }} />
              </button>
            </div>

            {pageLoading ? (
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            ) : (
              <>
                {book.contentType === "ImagePages" && (
                  <div className="bg-white p-2 shadow-2xl rounded-sm max-h-full aspect-[2/3] overflow-hidden">
                    {imagePages?.find(p => p.pageNumber === currentPage)?.imageUrl ? (
                      <img src={imagePages?.find(p => p.pageNumber === currentPage)?.imageUrl} className="w-full h-full object-contain" />
                    ) : (
                      <div className="flex items-center justify-center w-full h-full text-gray">Page {currentPage}</div>
                    )}
                  </div>
                )}
                {book.contentType === "TextPages" && (
                  <div className="bg-white p-8 md:p-12 shadow-2xl rounded-sm w-full max-w-3xl h-full overflow-y-auto">
                    <div dangerouslySetInnerHTML={{ __html: textPages?.find(p => p.pageNumber === currentPage)?.content }} className="prose max-w-none" />
                  </div>
                )}
              </>
            )}

            <div className="absolute bottom-6 bg-dark/80 text-white px-4 py-2 rounded-full text-sm font-bold backdrop-blur-sm">
              Page {currentPage} / {maxPage}
            </div>
          </div>

          {/* Right Panel: Actions/List (Optional sidebar within offcanvas?) */}
          {/* Keeping it simple as per original design likely just showed content area in sidebar or dedicated viewer */}
          <div className="w-80 bg-white border-l border-lightGray flex flex-col">
            <div className="p-4 border-b border-lightGray flex justify-between items-center bg-gray-50">
              <h6 className="font-bold text-dark m-0">Actions</h6>
              <button onClick={AddPage} className="p-2 bg-primary text-white rounded-lg shadow hover:bg-blue-600 transition-colors text-sm font-bold">
                <FontAwesomeIcon icon={faPlus} className="mr-2" /> Add Page
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-3">
                {allPages?.sort((a, b) => a.pageNumber - b.pageNumber).map(p => (
                  <div
                    key={p.id}
                    onClick={() => setCurrentPage(p.pageNumber)}
                    className={`cursor-pointer rounded-lg border-2 overflow-hidden relative group aspect-[2/3] flex items-center justify-center bg-light transition-all
                                        ${currentPage === p.pageNumber ? 'border-primary ring-2 ring-primary/20' : 'border-transparent hover:border-gray-300'}
                                    `}
                  >
                    {p.imageUrl ? (
                      <img src={p.imageUrl} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-xs text-left p-2 text-gray overflow-hidden h-full" dangerouslySetInnerHTML={{ __html: p.content || '...' }} />
                    )}
                    <div className="absolute bottom-0 left-0 w-full bg-black/60 text-white text-[10px] text-center py-1">
                      Page {p.pageNumber}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-lightGray">
              <button onClick={handleEditPage} className="w-full py-2 border border-warning text-warning rounded-lg hover:bg-warning hover:text-white transition-colors font-bold text-sm mb-2">
                <FontAwesomeIcon icon={faEdit} className="mr-2" /> Edit Current Page
              </button>
            </div>
          </div>
        </div>
      </OffCanvas>

      {/* 🔹 Add/Edit Image Page Modal */}
      <Model show={showAddImageModel} size="lg">
        <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-accent/20 text-white rounded-t-2xl">
          <h5 className="font-bold text-base m-0">{isEditingPage ? 'Edit Page Image' : 'Add New Page'}</h5>
          <button onClick={() => setShowImageModel(false)} className="text-white hover:text-white/80"><FontAwesomeIcon icon={faClose} /></button>
        </div>
        <div className="p-6 bg-light text-center">
          <div className="mx-auto mb-4 max-w-[300px] border border-gray-300 rounded-xl overflow-hidden bg-white shadow-sm">
            <SignatureUploader
              imageURL={coverPreview?.documentURL}
              onUploaded={saveCoverToDB}
              height={400}
              width={300}
              showEdit
            />
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button onClick={() => setShowImageModel(false)} className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray hover:bg-gray-50 font-medium">Cancel</button>
            <button onClick={saveImageAddPage} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 font-medium shadow-md">
              {isEditingPage ? 'Save Changes' : 'Add Page'}
            </button>
          </div>
        </div>
      </Model>

      {/* 🔹 Add/Edit Text Page Modal */}
      <Model show={showAddTextModel} size="xl">
        <div className="flex justify-between items-center px-4 py-3 bg-gradient-to-r from-slate-800 to-slate-900 border-b border-accent/20 text-white rounded-t-2xl">
          <h5 className="font-bold text-base m-0">{isEditingPage ? 'Edit Page Content' : 'Add New Text Page'}</h5>
          <button onClick={() => setShowTextModel(false)} className="text-white hover:text-white/80"><FontAwesomeIcon icon={faClose} /></button>
        </div>
        <div className="p-4 bg-white">
          <JoditEditor
            value={editorValue}
            config={editorConfig}
            tabIndex={1} // tabIndex of textarea
            onBlur={newContent => setEditorValue(newContent)}
            onChange={newContent => { }}
          />
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
            <button onClick={() => setShowTextModel(false)} className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray hover:bg-gray-50 font-medium">Cancel</button>
            <button onClick={saveTextAddPage} className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 font-medium shadow-md">
              {isEditingPage ? 'Save Changes' : 'Save Page'}
            </button>
          </div>
        </div>
      </Model>

    </div>
  );
};
